import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const StandardDialog = DialogPrimitive.Root

const StandardDialogPortal = DialogPrimitive.Portal

const StandardDialogClose = DialogPrimitive.Close

const StandardDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 duration-200",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
      className
    )}
    {...props}
  />
))
StandardDialogOverlay.displayName = "StandardDialogOverlay"

interface StandardDialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
}

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
};

const StandardDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  StandardDialogContentProps
>(({ className, children, size = "3xl", ...props }, ref) => (
  <StandardDialogPortal>
    <StandardDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 flex flex-col w-full translate-x-[-50%] translate-y-[-50%] border bg-background shadow-lg duration-200 sm:rounded-lg",
        "max-h-[90vh]",
        sizeClasses[size],
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </StandardDialogPortal>
))
StandardDialogContent.displayName = "StandardDialogContent"

const StandardDialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-between border-b px-4 md:px-6 py-3 flex-shrink-0",
      className
    )}
    {...props}
  >
    <div className="flex-1 min-w-0">
      {children}
    </div>
    <DialogPrimitive.Close className="ml-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  </div>
))
StandardDialogHeader.displayName = "StandardDialogHeader"

const StandardDialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex-1 overflow-y-auto p-4 md:p-6",
      className
    )}
    {...props}
  />
))
StandardDialogBody.displayName = "StandardDialogBody"

const StandardDialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-end gap-2 border-t bg-muted/50 px-4 md:px-6 py-3 flex-shrink-0",
      className
    )}
    {...props}
  />
))
StandardDialogFooter.displayName = "StandardDialogFooter"

const StandardDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
StandardDialogTitle.displayName = "StandardDialogTitle"

const StandardDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground mt-1", className)}
    {...props}
  />
))
StandardDialogDescription.displayName = "StandardDialogDescription"

// Re-export trigger from Radix for uncontrolled usage
const StandardDialogTrigger = DialogPrimitive.Trigger;

export {
  StandardDialog,
  StandardDialogPortal,
  StandardDialogOverlay,
  StandardDialogClose,
  StandardDialogTrigger,
  StandardDialogContent,
  StandardDialogHeader,
  StandardDialogBody,
  StandardDialogFooter,
  StandardDialogTitle,
  StandardDialogDescription,
}
